import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { Col, Row } from "antd";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { useContentBlockerTemplateDelete } from "../../../../../hooks/content-blocker/useContentBlockerTemplateDelete.js";
import { useContentBlockerTemplateMetaData } from "../../../../../hooks/content-blocker/useContentBlockerTemplateMetaData.js";
import { useTranslation } from "../../../../../others/i18n.js";
import { useStores } from "../../../../../store/root.js";
import { DEFAULT_PAGINATION_META_DATA } from "../../../../../types/template/meta-data.js";
import { QueryKeys } from "../../../../../util/query-keys.js";
import { TemplateMetaDataTable } from "../meta-data/templateMetaDataTable.js";
function ContentBlockerTemplateOverview() {
    // Generals
    const { t } = useTranslation();
    const { notificationStore } = useStores();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // Query
    const templateMetaQuery = useContentBlockerTemplateMetaData();
    // Recently created
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    let headline = searchParams.get(TemplateMetaDataField.Headline);
    useEffect(()=>{
        if (headline && !templateMetaQuery.isFetching) {
            notificationStore.add({
                type: "success",
                message: t("Content Blocker '{{headline}}' successfully created!", {
                    headline
                }),
                duration: 5,
                closable: true
            });
            // Remove the 'headline' search parameter from the URL
            headline = undefined;
            searchParams.delete(TemplateMetaDataField.Headline);
            const newSearch = searchParams.toString();
            location.search = newSearch;
            // Modify the current history entry without adding a new entry
            const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
            history.replaceState(null, "", newUrl);
        }
    }, [
        headline,
        templateMetaQuery
    ]);
    const [deleteId, setDeleteId] = useState(undefined);
    const useDelete = useContentBlockerTemplateDelete(deleteId);
    useEffect(()=>{
        if (useDelete.isFetched && useDelete.isSuccess) {
            setDeleteId(undefined);
            notificationStore.add({
                type: "success",
                message: t("Content blocker template deleted!"),
                closable: true,
                duration: 3
            });
            templateMetaQuery.refetch();
        } else if (useDelete.isError && useDelete.error) {
            notificationStore.addFromServerError(useDelete.error, t);
        }
    }, [
        useDelete
    ]);
    const title = t("Content Blocker");
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: title
                })
            }),
            /*#__PURE__*/ _jsx(Row, {
                children: /*#__PURE__*/ _jsx(Col, {
                    flex: 1,
                    children: /*#__PURE__*/ _jsx(TemplateMetaDataTable, {
                        loading: templateMetaQuery.isFetching,
                        tableTitle: {
                            title
                        },
                        templateMetaData: templateMetaQuery.data,
                        actions: {
                            favouriteToggleType: "Content-Blocker",
                            onFilterFavouriteToggle: ()=>{
                                templateMetaQuery.refetch();
                            },
                            templateType: "Content-Blocker",
                            onCreate: ()=>{
                                navigate("/rcb/content-blocker/create");
                            },
                            onEdit: (template)=>{
                                const editPath = `/rcb/content-blocker/edit/${template.identifier}`;
                                navigate(editPath);
                            },
                            onDelete: (template)=>{
                                queryClient.resetQueries(QueryKeys.deleteContentBlocker(template.id));
                                setDeleteId(template.id);
                            }
                        },
                        pagination: DEFAULT_PAGINATION_META_DATA
                    })
                })
            })
        ]
    });
}
export { ContentBlockerTemplateOverview };
